/**
 * PlatformSettings - Global LLM and Embedding configuration
 * Only accessible by super_admin (not sub_admin)
 */

import React, { useState } from 'react';
import {
    Settings, Moon, Sun, Database, Cpu, HardDrive, Wifi, Zap, CheckCircle, XCircle, Mail
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import LLMConfigTab from '../LLMConfigTab';
import EmailSettingsTab from '../EmailSettingsTab';

// System Health Card Component
function SystemHealthCard({ name, status, value, icon: Icon }) {
    const isHealthy = status === 'healthy';
    return (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
                <div className={cn(
                    "h-10 w-10 rounded-lg flex items-center justify-center",
                    isHealthy ? "bg-green-100" : "bg-red-100"
                )}>
                    <Icon className={cn(
                        "h-5 w-5",
                        isHealthy ? "text-green-600" : "text-red-600"
                    )} />
                </div>
                <div>
                    <p className="font-medium text-sm">{name}</p>
                    <p className="text-xs text-gray-500">{value}</p>
                </div>
            </div>
            {isHealthy ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
                <XCircle className="h-5 w-5 text-red-600" />
            )}
        </div>
    );
}

function PlatformSettings() {
    const [darkMode, setDarkMode] = useState(false);

    // System health data
    const systemHealth = [
        { name: 'Database', status: 'healthy', value: '99.9% uptime', icon: Database },
        { name: 'API Server', status: 'healthy', value: '45ms avg response', icon: Cpu },
        { name: 'Storage', status: 'healthy', value: '42% used', icon: HardDrive },
        { name: 'Network', status: 'healthy', value: 'All services online', icon: Wifi }
    ];

    return (
        <div className={cn("p-6 max-w-7xl mx-auto", darkMode && "dark bg-gray-900")}>
            {/* Header with Dark Mode Toggle */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2 bg-gradient-to-r from-[var(--g6-primary)] to-[var(--g6-secondary)] bg-clip-text text-transparent">
                        <Settings className="h-7 w-7 text-[var(--g6-primary)]" />
                        Platform Settings
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Configure global LLM, embedding providers, and email delivery. These settings override all tenant configurations.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setDarkMode(!darkMode)}
                    title="Toggle dark mode"
                >
                    {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </Button>
            </div>

            {/* System Health Monitoring */}
            <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <Zap className="h-5 w-5 text-[var(--g6-primary)]" />
                        <h3 className="font-bold text-lg">System Health</h3>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
                        <div className="h-2 w-2 rounded-full bg-green-600 animate-pulse"></div>
                        All Systems Operational
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {systemHealth.map((system, index) => (
                        <SystemHealthCard key={index} {...system} />
                    ))}
                </div>
            </div>

            {/* LLM Configuration - Using the full LLMConfigTab component */}
            <LLMConfigTab />

            {/* Email Configuration */}
            <EmailSettingsTab />

            {/* Info Note */}
            <div className="mt-4 p-4 bg-gradient-to-r from-[var(--g6-secondary)]/10 to-[var(--g6-primary)]/10 border border-[var(--g6-secondary)]/20 rounded-lg">
                <p className="text-sm text-gray-700">
                    <strong className="text-[var(--g6-primary)]">Platform Override:</strong> These settings will override all tenant-specific configurations.
                    Any changes here will affect all tenants immediately.
                </p>
            </div>
        </div>
    );
}

export default PlatformSettings;
